/**
 * Amazon商品ページURL（短縮URL含む）または素のASINから、ASINを抽出する共有ユーティリティ。
 *
 * 特急モード ([useExpressMode.ts](../../components/generation/express/useExpressMode.ts)) と
 * ASIN検索モーダル (AsinSearchModal) の両方から利用する。
 * 計画書: docs/features/product-add-modal-flow-plan.md §2.1
 */

export interface ExtractedAsin {
  asin: string;
  /** amzn.asia / amzn.to 等の短縮URLで、サーバーサイドのリダイレクト解決
   *  (`GET /amazon/resolve-url`) が必要な場合に true */
  needsResolve: boolean;
  /** needsResolve=true のとき、解決対象の元URL */
  originalUrl?: string;
}

/**
 * Amazon URLまたは素のASINからASINを抽出する。素のASINはそのまま返す。
 */
export function extractAsin(input: string): ExtractedAsin {
  const trimmed = input.trim();
  if (!trimmed) return { asin: trimmed, needsResolve: false };

  if (!trimmed.startsWith('http://') && !trimmed.startsWith('https://') && !trimmed.startsWith('www.')) {
    return { asin: trimmed, needsResolve: false };
  }

  try {
    const url = new URL(trimmed.startsWith('www.') ? `https://${trimmed}` : trimmed);

    const dpMatch = url.pathname.match(/\/(?:dp|gp\/product)\/([A-Z0-9]{10})(?:\/|$)/i);
    if (dpMatch) return { asin: dpMatch[1].toUpperCase(), needsResolve: false };

    // amzn.asia / amzn.to は短縮URLなのでサーバーサイドでリダイレクト解決が必要
    if (url.hostname === 'amzn.asia' || url.hostname === 'amzn.to') {
      return { asin: '', needsResolve: true, originalUrl: trimmed };
    }
  } catch {
    // Not a valid URL, return as-is
  }

  return { asin: trimmed, needsResolve: false };
}
